import React from 'react';
import { InputAdornment, InputProps } from '@material-ui/core';
import { Styled as StyledTextField } from 'components/core/forms/text-field/text-field.style';
import NumberFormat, { NumberFormatValues } from 'react-number-format';
import { TextField } from '../text-field';
import { MAX_ALLOWED_MONEY } from '../utils/form-fields';
import {
  factoryFormFieldComponent,
  FormFieldSpecs,
} from '../utils/factory-form-field-component';

export type TextFormFieldSpecs = FormFieldSpecs<{
  type?: 'text' | 'password' | 'tel';
  currency?: boolean;
  percentage?: boolean;
  disabled?: boolean;
  mask?: string;
  hasVisibilityToggle?: boolean;
  labelAddon?: React.ReactNode;
  leftLabel?: boolean;
  labelVariant?: number;
  customMaxAmount?: number;
  inputProps?: InputProps['inputProps'];
}>;

export const TextFieldController =
  factoryFormFieldComponent<TextFormFieldSpecs>(
    ({
      currency,
      defaultValue,
      disabled,
      error,
      hasVisibilityToggle,
      inputProps,
      label,
      labelAddon,
      labelVariant,
      leftLabel,
      mask,
      name,
      onBlur,
      onChange,
      percentage,
      placeholder,
      type,
      validations,
      value,
    }) => {
      const [isVisibilityHidden, setIsVisibilityHidden] = React.useState(
        defaultValue && hasVisibilityToggle,
      );

      const inputType = isVisibilityHidden ? 'password' : 'text';
      const muiInputProps: InputProps = {};

      const handleToggleVisibility = () =>
        setIsVisibilityHidden(!isVisibilityHidden);

      if (hasVisibilityToggle) {
        muiInputProps.endAdornment = (
          <InputAdornment position="end">
            <StyledTextField.EyeIconButton
              aria-label="toggle field visibility"
              disableRipple
              onClick={handleToggleVisibility}
              tabIndex="-1"
            >
              <StyledTextField.EyeIconVisibility />
            </StyledTextField.EyeIconButton>
          </InputAdornment>
        );
      }

      const textFieldProps = {
        currency,
        disabled,
        error: !!error,
        helperText: error?.message,
        label,
        labelAddon,
        labelVariant,
        leftLabel,
        name,
        onBlur,
        optional: !validations?.required,
        percentage,
        placeholder,
        type: hasVisibilityToggle ? inputType : type,
        value,
        InputProps: muiInputProps,
        inputProps,
      };

      if (mask) {
        return (
          <NumberFormat
            {...textFieldProps}
            customInput={TextField}
            mask="#"
            format={mask}
            type={hasVisibilityToggle ? inputType : type}
            onValueChange={(data) => {
              onChange(data.formattedValue, data.value);
            }}
          />
        );
      }

      if (currency) {
        const withValueLimit = ({ floatValue }: NumberFormatValues) =>
          floatValue ? floatValue <= MAX_ALLOWED_MONEY : true;

        return (
          <NumberFormat
            {...textFieldProps}
            customInput={TextField}
            allowNegative={false}
            decimalScale={2}
            fixedDecimalScale
            isAllowed={withValueLimit}
            prefix="$ "
            thousandSeparator
            onValueChange={(data) => {
              onChange(data.floatValue || '');
            }}
          />
        );
      }

      if (percentage) {
        const withValueLimit = ({ floatValue }: NumberFormatValues) =>
          floatValue ? floatValue <= 100 : true;

        return (
          <NumberFormat
            {...textFieldProps}
            customInput={TextField}
            allowNegative={false}
            decimalScale={0}
            isAllowed={withValueLimit}
            suffix="%"
            onValueChange={(data) => {
              onChange(data.floatValue || '');
            }}
          />
        );
      }

      return <TextField {...textFieldProps} onChange={onChange} />;
    },
    { componentName: 'TextFieldController' },
  );
